// Rubric editor — criteria (name, weight, description) + notes. Powers
// per-posting scoring. Saved rubrics are hashed by the backend so scores
// that were computed against an older rubric are marked stale.

import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, Loader2, Save, ChevronsUp, ChevronsDown, Info, Download, Upload } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";

const emptyCriterion = () => ({ name: "", weight: 1, description: "" });

export function RubricDialog({ open, onOpenChange, posting, onSaved }) {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [saving,  setSaving]  = useState(false);
  const [criteria, setCriteria] = useState([]);
  const [notes,    setNotes]    = useState("");
  const [tmplBusy, setTmplBusy] = useState(false); // load / save template

  const isSuperAdmin = user?.role === "super_admin";
  const deptKey = posting?.department_key || "";

  useEffect(() => {
    if (!open || !posting) return;
    (async () => {
      try {
        setLoading(true);
        const { data } = await api.get(`/jobs/${posting.posting_id}/rubric`);
        setCriteria((data.rubric?.criteria || []).map((c) => ({ ...c })));
        setNotes(data.rubric?.notes || "");
      } catch (e) {
        toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to load rubric");
      } finally { setLoading(false); }
    })();
  }, [open, posting]);

  const updateAt = (i, patch) => setCriteria((cs) => cs.map((c, j) => (j === i ? { ...c, ...patch } : c)));
  const removeAt = (i) => setCriteria((cs) => cs.filter((_, j) => j !== i));
  const move = (i, dir) => setCriteria((cs) => {
    const j = i + dir;
    if (j < 0 || j >= cs.length) return cs;
    const out = [...cs]; [out[i], out[j]] = [out[j], out[i]]; return out;
  });
  const add = () => setCriteria((cs) => [...cs, emptyCriterion()]);

  const save = async () => {
    // Cheap sanity — every criterion needs a name.
    const clean = criteria
      .map((c) => ({ name: (c.name || "").trim(), weight: Number(c.weight) || 1, description: (c.description || "").trim() }))
      .filter((c) => c.name);
    if (clean.length === 0) { toast.error("Add at least one criterion"); return; }
    try {
      setSaving(true);
      await api.put(`/jobs/${posting.posting_id}/rubric`,
        { criteria: clean, notes: notes.trim() });
      toast.success("Rubric saved");
      onSaved?.();
      onOpenChange(false);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Save failed");
    } finally { setSaving(false); }
  };

  // Iter 80c: load / save the department default template.
  const loadDeptTemplate = async () => {
    if (!deptKey) { toast.error("Posting has no department"); return; }
    try {
      setTmplBusy(true);
      const { data } = await api.get(`/jobs/departments/${deptKey}/rubric-template`);
      const tmpl = data?.template || {};
      setCriteria((tmpl.criteria || []).map((c) => ({ ...c })));
      setNotes(tmpl.notes || "");
      toast.success(`Loaded ${data?.department_name || "department"} template`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Load failed");
    } finally { setTmplBusy(false); }
  };

  const saveAsDeptTemplate = async () => {
    if (!deptKey) { toast.error("Posting has no department"); return; }
    const clean = criteria
      .map((c) => ({ name: (c.name || "").trim(), weight: Number(c.weight) || 1, description: (c.description || "").trim() }))
      .filter((c) => c.name);
    if (clean.length === 0) { toast.error("Add at least one criterion"); return; }
    if (!window.confirm(`Save this rubric as the default for new "${deptKey}" postings?`)) return;
    try {
      setTmplBusy(true);
      await api.put(`/jobs/departments/${deptKey}/rubric-template`,
        { criteria: clean, notes: notes.trim() });
      toast.success("Department template updated");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Save failed");
    } finally { setTmplBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="rubric-dialog">
        <DialogHeader>
          <DialogTitle>Rubric — {posting?.title}</DialogTitle>
          <DialogDescription>
            Claude scores each resume against these criteria. Weights are relative
            &mdash; higher = more important. Existing scores against an older rubric
            are marked stale until re-scored.
          </DialogDescription>
        </DialogHeader>
        {loading ? (
          <div className="py-8 flex justify-center"><Loader2 className="h-5 w-5 animate-spin text-slate-400" /></div>
        ) : (
          <div className="space-y-3">
            <div className="rounded-md border border-indigo-100 bg-indigo-50/60 p-2.5 text-xs text-indigo-900 flex items-start gap-2">
              <Info className="h-3.5 w-3.5 mt-0.5 shrink-0" />
              <span>Tip: keep criteria to 3&ndash;6 for tighter scores. Anything more and the model spreads its attention.</span>
            </div>
            <div className="flex flex-wrap items-center gap-2" data-testid="rubric-template-actions">
              <button type="button" onClick={loadDeptTemplate}
                      disabled={tmplBusy || !deptKey}
                      data-testid="rubric-load-dept-template"
                      className="inline-flex items-center gap-1 rounded-full border border-slate-200 bg-white px-2.5 py-1 text-[11px] font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200">
                {tmplBusy ? <Loader2 className="h-3 w-3 animate-spin" /> : <Download className="h-3 w-3" />}
                Load dept template
              </button>
              {isSuperAdmin && (
                <button type="button" onClick={saveAsDeptTemplate}
                        disabled={tmplBusy || !deptKey}
                        data-testid="rubric-save-dept-template"
                        className="inline-flex items-center gap-1 rounded-full border border-indigo-200 bg-indigo-50 px-2.5 py-1 text-[11px] font-medium text-indigo-800 hover:bg-indigo-100 disabled:opacity-50 dark:border-indigo-800 dark:bg-indigo-950/40 dark:text-indigo-200">
                  {tmplBusy ? <Loader2 className="h-3 w-3 animate-spin" /> : <Upload className="h-3 w-3" />}
                  Save as dept template
                </button>
              )}
              <span className="text-[10px] text-slate-500 dark:text-slate-400">
                {isSuperAdmin
                  ? "Templates auto-apply to new postings in this department."
                  : "Only super admins can update the department template."}
              </span>
            </div>
            <div className="space-y-2" data-testid="rubric-criteria-list">
              {criteria.map((c, i) => (
                <div key={i} className="rounded-md border border-slate-200 bg-white p-3 space-y-2"
                     data-testid={`rubric-criterion-${i}`}>
                  <div className="flex items-center gap-2">
                    <Input value={c.name}
                           onChange={(e) => updateAt(i, { name: e.target.value })}
                           placeholder="Criterion name (e.g. Must-have skills)"
                           data-testid={`rubric-criterion-name-${i}`}
                           className="flex-1" />
                    <div className="flex items-center gap-1 text-xs text-slate-500">
                      <span>Weight</span>
                      <Input type="number" min={1} max={10}
                             value={c.weight}
                             onChange={(e) => updateAt(i, { weight: Number(e.target.value) })}
                             data-testid={`rubric-criterion-weight-${i}`}
                             className="w-16 h-8 text-center" />
                    </div>
                    <div className="inline-flex gap-0.5">
                      <Button size="sm" variant="ghost" className="h-8 w-8 p-0"
                              onClick={() => move(i, -1)} disabled={i === 0}
                              data-testid={`rubric-criterion-up-${i}`}
                              title="Move up">
                        <ChevronsUp className="h-3.5 w-3.5" />
                      </Button>
                      <Button size="sm" variant="ghost" className="h-8 w-8 p-0"
                              onClick={() => move(i, 1)} disabled={i === criteria.length - 1}
                              data-testid={`rubric-criterion-down-${i}`}
                              title="Move down">
                        <ChevronsDown className="h-3.5 w-3.5" />
                      </Button>
                      <Button size="sm" variant="ghost" className="h-8 w-8 p-0 text-rose-600 hover:bg-rose-50"
                              onClick={() => removeAt(i)}
                              data-testid={`rubric-criterion-remove-${i}`}
                              title="Remove">
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  </div>
                  <Textarea value={c.description}
                            onChange={(e) => updateAt(i, { description: e.target.value })}
                            placeholder="What does a strong signal look like on this criterion? (optional)"
                            data-testid={`rubric-criterion-desc-${i}`}
                            rows={2}
                            className="text-xs" />
                </div>
              ))}
            </div>
            <Button variant="outline" onClick={add} data-testid="rubric-add-criterion" className="w-full gap-1">
              <Plus className="h-3.5 w-3.5" /> Add criterion
            </Button>
            <div className="space-y-1.5">
              <Label>Reviewer notes (shared with the LLM)</Label>
              <Textarea value={notes}
                        onChange={(e) => setNotes(e.target.value)}
                        placeholder="Anything the model should keep in mind — dealbreakers, gotchas, tone."
                        rows={3}
                        data-testid="rubric-notes" />
            </div>
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>Cancel</Button>
          <Button onClick={save} disabled={saving || loading} data-testid="rubric-save" className="gap-1.5">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save rubric
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
